import Card from 'components/atoms/Card';
import Markdown from 'components/atoms/Markdown';
import Typography from 'components/atoms/Typography';
import { ellipsisUtil } from 'utils/common';
import {
  Templates,
  TemplateType,
  DownloadTemplateConfig,
  LinkTemplateConfig,
  VideoTemplateConfig,
} from 'utils/gloo-types';
import Icon from 'components/atoms/Icon';
import { IconName } from 'components/atoms/Icon/all';
import { getImageOrThumbnailFromTemplate } from 'utils/discover';
import { useEffect, useState } from 'react';
import Button from 'components/atoms/Button';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import CloudinaryVideoPlayer from '../CloudinaryVideoPlayer';

interface TemplatePreviewCardProps {
  template: Partial<Templates>;
  info?: { property: string; value: any; icon: string }[];
}

const CalloutMapping = {
  [TemplateType.KEYWORD]: 'Keyword',
  [TemplateType.BROADCAST]: 'Broadcast',
  [TemplateType.SERIES]: 'Series',
  [TemplateType.LINK]: 'Link',
  [TemplateType.DOWNLOAD]: 'Download',
  [TemplateType.SURVEY]: 'Survey',
  [TemplateType.SCHOLARSHIP]: 'Scholarship',
  [TemplateType.VIDEO]: 'Video',
  [TemplateType.ASSESSMENT]: 'Assessment',
};

function TemplateVideoPlayer({ template }: { template: Partial<Templates> }) {
  const config = template.config as VideoTemplateConfig;
  if (!config?.url) return null;
  return (
    <CloudinaryVideoPlayer
      url={config.url}
      thumbnailImage={getImageOrThumbnailFromTemplate(template)}
      config={!config.canDownload ? { file: { attributes: { controlsList: 'nodownload' } } } : undefined}
    />
  );
}

function TemplateDownloadButton({ template }: TemplatePreviewCardProps) {
  const config = template.config as VideoTemplateConfig;
  const [downloadStarted, setDownloadStarted] = useState(false);
  useEffect(() => {
    if (downloadStarted) {
      const timeout = setTimeout(() => setDownloadStarted(false), 20 * 1000);
      return () => clearTimeout(timeout);
    }
  }, [downloadStarted]);

  if (!config.canDownload || !config.downloadUrl) {
    return null;
  }

  return (
    <div>
      <Button
        type="secondary"
        icon="download"
        size="small"
        dataDurableId="templateVideoDownloadButton"
        text="Download Video"
        disabled={downloadStarted}
        onClick={async () => {
          // Cloudinary docs: https://cloudinary.com/documentation/transformation_reference#fl_attachment
          const downloadFlag = 'fl_attachment';
          const downloadUrl = config?.downloadUrl;

          if (!downloadUrl) {
            toast.error('Unable to download media because URL is missing');
            return;
          }

          const downloadUrlContainsFlag = !!downloadUrl.match(new RegExp(`${downloadFlag}`));

          const flagOffset = downloadUrl.match(/d\/v/)?.index + 2;
          let finalUrl = downloadUrl;

          // Insert download flag into the Cloudinary downloadUrl at ...upload/(here)/vfilehash
          if (flagOffset && !downloadUrlContainsFlag) {
            finalUrl = `${downloadUrl.slice(0, flagOffset)}${downloadFlag}/${downloadUrl.slice(flagOffset)}`;
          }

          // Ensure https is used
          finalUrl = finalUrl.replace(/^http:\/\//i, 'https://');

          // Get the file extension from the downloadUrl to build filename
          const fileExtension = downloadUrl.substring(downloadUrl.lastIndexOf('.'));
          const filename = `${template.title}${fileExtension}`;
          saveAs(finalUrl, filename);

          // Temporarily disable the download button for 20 seconds
          setDownloadStarted(true);
        }}
      />
      {downloadStarted && (
        <Typography variant="xs" className="pt-1">
          Your download has started, this could take a few minutes.
        </Typography>
      )}
    </div>
  );
}

function TemplatePreviewCard({ template, info }: TemplatePreviewCardProps) {
  return (
    <div className="w-full py-4">
      <Card
        noPadding
        rounded="none"
        elevation={0}
        className="mb-6 overflow-hidden border cursor-pointer border-blueLightest"
        onClick={() => {
          if (template.type === TemplateType.LINK) {
            const config = template.config as LinkTemplateConfig;
            window.open(config.url);
          } else if (template.type === TemplateType.DOWNLOAD) {
            const config = template.config as DownloadTemplateConfig;
            window.open(config.url);
          }
        }}
      >
        {template.type === TemplateType.VIDEO && (template.config as VideoTemplateConfig)?.url ? (
          <TemplateVideoPlayer template={template} />
        ) : (
          <div className="relative imageWrapper">
            <img
              alt={template?.title}
              className="object-cover object-bottom w-full h-full bg-white"
              src={getImageOrThumbnailFromTemplate(template)}
            />
            <Typography variant="h5" className="absolute top-0 left-0 px-2 py-1 m-2 bg-white rounded-sm uppercase">
              {CalloutMapping[template.type] || template.type}
            </Typography>
          </div>
        )}
      </Card>

      {info &&
        info.map((infoProperty) => {
          return (
            <div key={`${infoProperty.property}`} className="flex flex-row pt-1 flex-1 mx-1">
              <Typography variant="h2">{`${infoProperty.property}:`}</Typography>
              <Typography variant="base" className="pb-2 ml-2">
                {infoProperty.value}
              </Typography>
              <Icon className="mr-1 text-black ml-3" name={infoProperty.icon as IconName} size={20} />
            </div>
          );
        })}
      {template.type === TemplateType.VIDEO && <TemplateDownloadButton template={template} />}
      {template.type !== TemplateType.SURVEY && (
        <Typography variant="h2" className="mt-4" title={template?.title}>
          {ellipsisUtil(template?.title, 60)}
        </Typography>
      )}
      <Markdown className="mt-4" content={template?.longDescription} />
    </div>
  );
}

export default TemplatePreviewCard;
